import React, { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import UserIcon from '../../public/static/images/icon-user-in-circle--black.svg';
import useToggle from '../../hooks/useToggle';
import { useProfileLogout } from '../../hooks/useProfileLogOut';
import { makeUrlWithLang } from '../../utils/helpers';
import config from '../../config';
import ButtonPlaceholder from '../Placeholders/ButtonPlaceholder';
import {
  DropdownItem,
  DropdownItemsContainer,
  SelectedButton,
  SelectedButtonUserIcon,
  DropDownProfileLink,
  UserContainer,
  UserItem,
  SignInButton
} from './styled';

const LoginButton = ({
  isAuth = false,
  isLoadingProfile,
  profileData,
  isAuthInProgress
}) => {
  const [isShowDropdown, toggleDropdown] = useToggle(false);
  const router = useRouter();
  const { handleLogout } = useProfileLogout();
  const currentPath = router.asPath.split('#')[0];
  const {
    t,
    i18n: { language: lang }
  } = useTranslation();
  const dropDownRef = useRef(null);
  const { protocol, host } = config;
  const hostString = path => makeUrlWithLang({ protocol, host, path, lang });

  const handleProfileClick = () => {
    router.push(`${currentPath}#authentication`, undefined, {
      shallow: true
    });
  };

  const dropdownItemsList = [
    {
      title: (
        <DropDownProfileLink
          href={hostString('/profile')}
          data-cy="header-profile-link"
          data-stat="header-profile-link"
        >
          {t('common.cabinet')}
        </DropDownProfileLink>
      ),
      onClick: null
    },
    {
      title: (
        <DropdownItem onClick={handleLogout}>{t('common.logout')}</DropdownItem>
      ),
      onClick: handleLogout
    }
  ];

  const handleDropDownOutsideClick = event => {
    if (
      dropDownRef.current &&
      !dropDownRef.current.contains(event.target) &&
      isShowDropdown
    ) {
      toggleDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleDropDownOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleDropDownOutsideClick);
    };
  }, [isShowDropdown]);

  if (isLoadingProfile || isAuthInProgress) return <ButtonPlaceholder />;

  return (
    <>
      {isAuth ? (
        <SelectedButton
          $isShowDropdown={isShowDropdown}
          ref={dropDownRef}
          onClick={toggleDropdown}
          onKeyPress={toggleDropdown}
          role="button"
          tabIndex="-1"
        >
          <SelectedButtonUserIcon>
            <Icon
              IconComponent={UserIcon}
              width={35}
              height={34}
              indent={false}
            />
          </SelectedButtonUserIcon>
          {isShowDropdown && (
            <DropdownItemsContainer>
              <UserContainer>
                <UserItem>{profileData?.phone}</UserItem>
                <UserItem>{profileData?.email}</UserItem>
              </UserContainer>
              {dropdownItemsList.map(({ title, onClick }, index) => (
                <React.Fragment key={index}>{title}</React.Fragment>
              ))}
            </DropdownItemsContainer>
          )}
        </SelectedButton>
      ) : (
        <SignInButton
          onClick={handleProfileClick}
          data-cy="header-profile-btn"
          data-stat="header-profile-btn"
        >
          {t('common.login')}
        </SignInButton>
      )}
    </>
  );
};

LoginButton.propTypes = {
  isAuth: PropTypes.bool,
  isLoadingProfile: PropTypes.bool,
  isAuthInProgress: PropTypes.bool,
  profileData: PropTypes.object
};

export default LoginButton;
